import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MultiSelectField, matchesLabel } from '@ui/components/ui/multi-select';
import { cn } from '@ui/lib/utils';

import { fitPills } from './fitPills';
import { PillContent, pillClass } from './pill';

const PILL_GAP = 6;
// The dropdown's `w-40`, its margin and the last pill's: only the first line shares its width with it.
const DROPDOWN_WIDTH = 172;

export interface ProductPickerProps {
  /** Every product that can be picked, by name. */
  products: string[];
  /** The products offered as pills, in order; the rest are behind Other product. */
  pills: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
  /** Pills shown greyed out and not pickable, with `disabledTitle` saying why. */
  disabled?: string[];
  disabledTitle?: string;
}

/** Product pills, as many unpicked ones as fit on one line, and an Other product dropdown beside them for everything else. */
const ProductPicker = ({
  products,
  pills,
  selected,
  onChange,
  disabled = [],
  disabledTitle,
}: ProductPickerProps): JSX.Element => {
  const { t } = useTranslation();
  const rowRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState<string[] | null>(null);
  // Suggestions come first: while something picked from the dropdown is ticked, the unpicked ones step aside.
  const [fromList, setFromList] = useState<string[]>([]);
  const pickedOther = fromList.some(product => selected.includes(product));

  const row = useMemo(
    () => [
      ...pills.filter(product => !pickedOther || selected.includes(product)),
      ...selected.filter(product => !pills.includes(product)),
    ],
    [pills, selected, pickedOther],
  );

  const fit = useCallback(() => {
    const box = rowRef.current;
    const measure = measureRef.current;
    if (!box || !measure) return;
    const available = box.clientWidth - DROPDOWN_WIDTH;
    const widths = new Map(
      Array.from(measure.children).map(child => [
        (child as HTMLElement).dataset.pill ?? '',
        (child as HTMLElement).offsetWidth,
      ]),
    );
    // Nothing measured is jsdom or a hidden dialog: show the whole row rather than none of it.
    const next = available > 0 ? fitPills(row, pill => widths.get(pill) ?? 0, selected, available, PILL_GAP) : row;
    setShown(previous => (previous && previous.join('\n') === next.join('\n') ? previous : next));
  }, [row, selected]);

  useLayoutEffect(() => {
    fit();
    if (typeof ResizeObserver === 'undefined' || !rowRef.current) return undefined;
    const observer = new ResizeObserver(fit);
    observer.observe(rowRef.current);
    if (measureRef.current) observer.observe(measureRef.current);
    return () => observer.disconnect();
  }, [fit]);

  const visible = shown ?? row;
  // With no pills there is nothing for "other" to be other than, so the dropdown is the whole question.
  const dropdownLabel = t(visible.length ? 'phillReport.otherProduct' : 'phillReport.pickProduct');
  const toggle = (product: string): void =>
    onChange(selected.includes(product) ? selected.filter(name => name !== product) : [...selected, product]);
  // What was ticked in the list stays in it, so a row does not vanish from under the cursor as it becomes a pill.
  const listed = products.filter(product => !visible.includes(product) || fromList.includes(product));
  // Suggestions sit at the bottom, ticked or not, so a row never moves under the cursor.
  const suggested = listed.filter(product => pills.includes(product));
  const others = [...listed.filter(product => !suggested.includes(product)), ...suggested].map(product => ({
    value: product,
    label: product,
  }));

  return (
    // A float, so the pills run beside the dropdown on the first line and under it after that.
    <div ref={rowRef} className={cn('relative flow-root', visible.length && '-mb-1.5')}>
      <div aria-hidden ref={measureRef} className="pointer-events-none invisible absolute top-0 left-0 flex">
        {row.map(product => (
          <span key={product} data-pill={product} className={pillClass(selected.includes(product))}>
            <PillContent pressed={selected.includes(product)} isDisabled={disabled.includes(product)}>
              {product}
            </PillContent>
          </span>
        ))}
      </div>
      <MultiSelectField
        clearable={false}
        id="phill-other-product"
        className={cn('gap-0', visible.length && 'float-right ml-1.5 w-40')}
        label={<span className="sr-only">{dropdownLabel}</span>}
        options={others}
        value={selected.filter(product => others.some(option => option.value === product))}
        onChange={next => {
          setFromList(current => [...current, ...next.filter(product => !current.includes(product))]);
          onChange([...selected.filter(product => !others.some(option => option.value === product)), ...next]);
        }}
        summaryThreshold={0}
        renderCountSummary={() => dropdownLabel}
        emptyLabel={dropdownLabel}
        searchPlaceholder={t('phillReport.searchProducts')}
        emptySearchLabel={t('noProductsFound')}
        filter={matchesLabel}
        align="end"
        contentClassName="w-64"
      />
      {visible.map(product => (
        <button
          key={product}
          type="button"
          aria-pressed={selected.includes(product)}
          className={cn(
            pillClass(selected.includes(product)),
            'mr-1.5 mb-1.5 align-top disabled:cursor-not-allowed disabled:opacity-45',
          )}
          onClick={() => toggle(product)}
          disabled={disabled.includes(product)}
          title={disabled.includes(product) ? disabledTitle : undefined}
        >
          <PillContent pressed={selected.includes(product)} isDisabled={disabled.includes(product)}>
            {product}
          </PillContent>
        </button>
      ))}
    </div>
  );
};

export default ProductPicker;
